import { Table } from '@tanstack/react-table';
import { uniq } from 'lodash';

import { AssetClass, AssetType, SubAssetType } from '../_openapi/portfolio-optimizer';

const AssetClassTypeSubTypeMap: { [k in AssetClass]: { [j in AssetType]?: SubAssetType[] } } = {
    Bond: {
        Treasury: ['Bill', 'Bond', 'Note'],
        Muni: [],
    },
    Equity: {
        ADR: [],
        'Common Stock': [],
        ETF: ['ETF', 'ETN', 'ETV'],
        'Mutual Fund': [],
        Preferred: [],
        SPAC: [],
        Warrant: [],
        REIT: [],
    },
    Futures: {
        'Interest Rates': [],
        Volatility: [],
    },
    Options: {
        Equity: [],
        Futures: [],
        Index: [],
    },
    Other: {
        Commodities: [],
    },
};

export const getAvailableAssetClasses = (table?: Table<unknown>) => {
    if (!table) {
        return Object.values(AssetClass);
    }

    const assetTypeFilters = table.getColumn('assetType')?.getFilterValue() as string[] | undefined;
    const subAssetTypeFilters = table.getColumn('subAssetType')?.getFilterValue() as string[] | undefined;

    let values = Object.values(AssetClass);
    if (subAssetTypeFilters?.length) {
        values = uniq(
            subAssetTypeFilters.map((val) => {
                return getAssetClassByAssetType(getAssetTypeBySubAssetType(val as SubAssetType));
            }),
        );
    } else if (assetTypeFilters?.length) {
        values = uniq(
            assetTypeFilters.map((val) => {
                return getAssetClassByAssetType(val as AssetType);
            }),
        );
    }
    return values.sort();
};

export const getAvailableAssetTypes = (table?: Table<unknown>) => {
    if (!table) {
        return Object.values(AssetType);
    }

    const assetClassFilters = table.getColumn('assetClass')?.getFilterValue() as string[] | undefined;
    const subAssetTypeFilters = table.getColumn('subAssetType')?.getFilterValue() as string[] | undefined;

    let values = Object.values(AssetType);
    if (subAssetTypeFilters?.length) {
        values = uniq(
            subAssetTypeFilters.map((val) => {
                return getAssetTypeBySubAssetType(val as SubAssetType);
            }),
        );
    } else if (assetClassFilters?.length) {
        values = uniq(
            assetClassFilters
                .map((val) => {
                    return getObjectKeys(AssetClassTypeSubTypeMap[val as AssetClass]);
                })
                .flat(),
        );
    }
    return values.sort();
};

export const getAvailableSubAssetTypes = (table?: Table<unknown>) => {
    if (!table) {
        return Object.values(SubAssetType);
    }

    const assetClassFilters = table.getColumn('assetClass')?.getFilterValue() as string[] | undefined;
    const assetTypeFilters = table.getColumn('assetType')?.getFilterValue() as string[] | undefined;

    let values = Object.values(SubAssetType);
    if (assetTypeFilters?.length) {
        values = uniq(
            assetTypeFilters
                .map((val) => {
                    const assetClass = getAssetClassByAssetType(val as AssetType);
                    return AssetClassTypeSubTypeMap[assetClass][val as AssetType];
                })
                .flat(),
        ) as SubAssetType[];
    } else if (assetClassFilters?.length) {
        values = uniq(
            assetClassFilters
                .map((val) => {
                    return Object.values(AssetClassTypeSubTypeMap[val as AssetClass]);
                })
                .flat(2),
        );
    }
    return values.sort();
};

const getAssetClassByAssetType = (assetType: AssetType): AssetClass => {
    return getObjectKeys(AssetClassTypeSubTypeMap).find((assetClass) => {
        return getObjectKeys(AssetClassTypeSubTypeMap[assetClass]).includes(assetType);
    }) as AssetClass;
};

const getAssetTypeBySubAssetType = (subAssetType: SubAssetType): AssetType => {
    return getObjectKeys(AssetClassTypeSubTypeMap).reduce<AssetType | undefined>((result, assetClass) => {
        return (
            result ||
            getObjectKeys(AssetClassTypeSubTypeMap[assetClass]).find((assetType) => {
                return AssetClassTypeSubTypeMap[assetClass][assetType]?.includes(subAssetType);
            })
        );
    }, undefined) as AssetType;
};

const getObjectKeys = <T extends object>(obj: T) => Object.keys(obj) as (keyof T)[];
